import { createContext, useContext, useEffect, useMemo, useRef, useState, ReactNode } from 'react';
import { User, Session, AuthError } from '@supabase/supabase-js';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';
import { checkRateLimit } from '@/lib/rateLimit';
import { logError } from '@/lib/errorLogger';
import { logger } from '@/lib/logger';
import { useLoadingState } from '@/hooks/useLoadingState';
import { resetPrefetchedTabs } from '@/hooks/useTabDataPrefetcher';
import { sessionBackupStore } from '@/lib/sessionBackupStore';
import { setSwAuthToken, clearSwAuthToken } from '@/lib/swAuthBridge';
import { clearQueryCache } from '@/lib/queryPersistence';
import { getBetaAcknowledgmentStatus, markBetaAcknowledgmentFreshSignIn } from '@/lib/betaAcknowledgmentGate';
import { logOfflineBootstrapPath, writeAccessCache } from '@/lib/offlineAccessCache';
import { analytics } from '@/lib/analytics';
import { persistPendingSignupConsent } from '@/lib/signupConsent';

// ProtectedRoute is the only safe place to write allow-cache entries because it
// verifies beta/legal gates first. Auth should only clear old unsafe allow caches.
const ACCESS_CACHE_STORAGE_PREFIXES = ["llx:access:v2:", "llx:access:v3:", "llx:access:v4:", "llx:access:v5:", "llx:access:v6:", "llx:access:v7:", "llx:access:v8:", "llx:access:v9:"];
const CURRENT_ACCESS_CACHE_STORAGE_PREFIX = "llx:access:v9:";
const cacheAccessResult = (userId: string, hasAccess: boolean) => {
  if (!hasAccess) return;
  try {
    ACCESS_CACHE_STORAGE_PREFIXES.forEach((prefix) => {
      localStorage.removeItem(`${prefix}${userId}`);
    });
    localStorage.setItem(
      `${CURRENT_ACCESS_CACHE_STORAGE_PREFIX}${userId}`,
      JSON.stringify({ hasAccess: true, checkedAt: Date.now() })
    );
  } catch {
    // ignore storage errors
  }
  writeAccessCache(userId);
};

type AuthBootstrapStatus = 'booting' | 'refreshing' | 'ready' | 'stalled';
type AuthBootstrapReason =
  | 'session_found'
  | 'no_session'
  | 'offline_no_session'
  | 'timeout'
  | 'error';

const hasStoredAuthToken = (): boolean => {
  try {
    // Boolean-only check: do not log keys or values.
    return Object.keys(localStorage).some(
      (k) => k.startsWith('sb-') && k.includes('auth-token')
    );
  } catch {
    return false;
  }
};

const readStoredSupabaseSession = (): Session | null => {
  try {
    const authKey = Object.keys(localStorage).find(
      (k) => k.startsWith('sb-') && k.includes('auth-token')
    );
    if (!authKey) return null;

    const parsed = JSON.parse(localStorage.getItem(authKey) ?? 'null') as unknown;
    const storedSession = (
      parsed && typeof parsed === 'object' && 'currentSession' in parsed
        ? (parsed as { currentSession?: Session | null }).currentSession
        : parsed
    ) as Session | null;

    if (
      storedSession?.user?.id &&
      typeof storedSession.access_token === 'string' &&
      typeof storedSession.refresh_token === 'string'
    ) {
      return storedSession as Session;
    }
  } catch {
    // ignore malformed/blocked storage
  }
  return null;
};

class TimeoutError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'TimeoutError';
  }
}

const withTimeout = async <T,>(promise: Promise<T>, ms: number, label: string): Promise<T> => {
  let timer: number | undefined;
  const timeoutPromise = new Promise<T>((_, reject) => {
    timer = window.setTimeout(() => reject(new TimeoutError(label)), ms);
  });
  try {
    return await Promise.race([promise, timeoutPromise]);
  } finally {
    if (timer) window.clearTimeout(timer);
  }
};

const isDefinitivelyNoSessionError = (err: unknown): boolean => {
  const msg = err instanceof Error ? err.message : String(err ?? '');
  // Conservative: only treat these as definitive.
  return (
    msg.toLowerCase().includes('invalid refresh token') ||
    msg.toLowerCase().includes('refresh token not found') ||
    msg.toLowerCase().includes('session not found')
  );
};

interface AuthContextType {
  user: User | null;
  session: Session | null;
  loading: boolean;
  /** True once the initial getSession() has completed - guards should wait for this */
  isAuthReady: boolean;
  /** Auth bootstrap lifecycle: used to prevent redirect loops in PWAs */
  bootstrapStatus: AuthBootstrapStatus;
  bootstrapReason: AuthBootstrapReason | null;
  retryAuthInit: () => void;
  signIn: (email: string, password: string, honeypot?: string) => Promise<{ error: AuthError | Error | null }>;
  signUp: (email: string, password: string, honeypot?: string) => Promise<{ error: AuthError | Error | null }>;
  signOut: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider = ({ children }: { children: ReactNode }) => {
  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [loading, setLoading] = useState(true);
  // Explicit flag: true only after first getSession() resolves
  const [isAuthReady, setIsAuthReady] = useState(false);
  const [bootstrapStatus, setBootstrapStatus] = useState<AuthBootstrapStatus>('booting');
  const [bootstrapReason, setBootstrapReason] = useState<AuthBootstrapReason | null>(null);
  const [bootstrapNonce, setBootstrapNonce] = useState(0);
  const setAuthLoading = useLoadingState(state => state.setLoading);

  // Refs for timeout callbacks (avoid stale-closure false stalls)
  const isAuthReadyRef = useRef(isAuthReady);
  const hasSessionRef = useRef(!!session?.user);
  const offlineSessionRestoredRef = useRef(false);
  useEffect(() => {
    isAuthReadyRef.current = isAuthReady;
    hasSessionRef.current = !!session?.user;
  }, [isAuthReady, session]);

  // Helper to save user's timezone to profile.
  // CRITICAL: Only write on first-ever sign-in.
  // The `profiles.timezone` column defaults to 'UTC' NOT NULL, so a brand-new
  // profile arrives with 'UTC' rather than NULL. We treat a stored value of
  // 'UTC' as "not yet set" ONLY for freshly-created accounts (profile created
  // within the last 10 minutes). Subsequent timezone changes flow through the
  // TimezoneChangeBanner / Trip Mode UI so the device's auto-detected zone
  // never silently overwrites the user's chosen home timezone (e.g. while
  // traveling).
  const saveUserTimezone = async (userId: string, userCreatedAt?: string) => {
    try {
      const { data, error: readErr } = await supabase
        .from('profiles')
        .select('timezone, created_at')
        .eq('id', userId)
        .maybeSingle();
      if (readErr) {
        logger.error('Failed to read profile timezone', readErr);
        return;
      }
      const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
      if (!timezone) return;

      const stored = data?.timezone;
      // Determine if this profile was just created (first-ever sign-in window).
      const referenceCreatedAt = data?.created_at ?? userCreatedAt ?? null;
      const isFreshProfile = referenceCreatedAt
        ? (Date.now() - new Date(referenceCreatedAt).getTime()) < 10 * 60 * 1000
        : false;

      if (stored && stored !== 'UTC') {
        // User already has a real stored home timezone — do not overwrite.
        return;
      }
      if (stored === 'UTC') {
        // Only auto-correct the 'UTC' default for brand-new accounts, and only
        // when the detected zone actually differs (avoid a redundant write for
        // users who genuinely are in UTC).
        if (!isFreshProfile || timezone === 'UTC') return;
      }

      await supabase
        .from('profiles')
        .update({ timezone })
        .eq('id', userId);
      logger.log(`Saved initial timezone ${timezone} for user ${userId} (previous: ${stored ?? 'null'})`);
    } catch (err) {
      logger.error('Failed to save timezone', err);
    }
  };

  useEffect(() => {
    setAuthLoading('auth', true);
    
    // Dev instrumentation
    const authStartTime = performance.now();
    const tokenKeyExists = hasStoredAuthToken();
    if (import.meta.env.DEV) {
      logger.log('[AuthBootstrap] hasStoredAuthToken:', tokenKeyExists);
    }
    
    // Set up auth state listener FIRST
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      (event, session) => {
        const elapsed = performance.now() - authStartTime;
        logger.log('[AuthContext] onAuthStateChange:', event, `(${elapsed.toFixed(0)}ms)`);

        // Mobile PWAs can emit a late null INITIAL_SESSION while offline even
        // after our bootstrap fast-path restored a valid cached session. Do not
        // let that non-authoritative null event erase the offline session and
        // send the route gates back to the splash screen.
        if (
          event === 'INITIAL_SESSION' &&
          !session?.user &&
          typeof navigator !== 'undefined' &&
          navigator.onLine === false &&
          (offlineSessionRestoredRef.current || hasSessionRef.current || !!readStoredSupabaseSession()?.user)
        ) {
          logger.log('[AuthContext] Ignoring offline null INITIAL_SESSION; cached session remains active');
          return;
        }

        setSession(session);
        setUser(session?.user ?? null);

        // Mark auth as "ready" when we have a deterministic auth event.
        // IMPORTANT: Never sign out or clear storage here; transient failures must not boot users.
        if (event === 'SIGNED_OUT') {
          offlineSessionRestoredRef.current = false;
          setBootstrapStatus('ready');
          setBootstrapReason('no_session');
          setIsAuthReady(true);
          setLoading(false);
          setAuthLoading('auth', false);
          // Clear IndexedDB backup on explicit sign out
          sessionBackupStore.clearBackup();
          // Revoke the SW's cached auth token so background ACKs stop.
          setTimeout(() => { clearSwAuthToken(); }, 0);
          // Clear cached subscription state so the next user does not inherit
          // the previous user's tier (prevents e.g. a tester's "lifetime"
          // cache showing up on a freshly created account).
          setTimeout(() => {
            import('@/hooks/useSubscription')
              .then(m => m.clearSubscriptionCaches?.())
              .catch(() => { /* ignore */ });
          }, 0);
        }

        if (
          (event === 'INITIAL_SESSION' ||
            event === 'SIGNED_IN' ||
            event === 'TOKEN_REFRESHED' ||
            event === 'USER_UPDATED') &&
          session?.user
        ) {
          offlineSessionRestoredRef.current = false;
          setBootstrapStatus('ready');
          setBootstrapReason('session_found');
          setIsAuthReady(true);
          setLoading(false);
          setAuthLoading('auth', false);
          
          // Backup session to IndexedDB for PWA persistence (deferred to avoid deadlock)
          setTimeout(() => {
            const rememberMe = localStorage.getItem('lupuslogix_remember_me') !== 'false';
            sessionBackupStore.backupSession(session, rememberMe);
          }, 0);

          // Sync the access token to IndexedDB so the push service worker
          // can authenticate ACK requests to the ack-push-receipt function.
          if (session.access_token) {
            setTimeout(() => { setSwAuthToken(session.access_token); }, 0);
          }
        }
        
        // Detect first-time signup (email verification flow):
        // When user verifies email and signs in for the first time, set the signup flag
        // so they get routed to the marketing page instead of directly to dashboard.
        if (event === 'SIGNED_IN' && session?.user) {
          const userCreatedAt = new Date(session.user.created_at);
          const secondsSinceCreation = (Date.now() - userCreatedAt.getTime()) / 1000;
          const alreadyOnboarded = localStorage.getItem('lupuslogix_onboarded') === 'true';
          const alreadyFlagged = localStorage.getItem('lupuslogix_just_signed_up') === 'true';
          
          // If account was created within 5 minutes and user hasn't been onboarded yet,
          // this is likely a first-time email verification login
          if (secondsSinceCreation < 300 && !alreadyOnboarded && !alreadyFlagged) {
            localStorage.setItem('lupuslogix_just_signed_up', 'true');
            localStorage.removeItem('lupuslogix_dashboard_tour');
            logger.log('[AuthContext] First-time signup detected, setting onboarding flag');
          }
        }
        
        // Save timezone on sign in/sign up (deferred to avoid deadlock)
        if ((event === 'SIGNED_IN' || event === 'USER_UPDATED') && session?.user) {
          const createdAt = session.user.created_at;
          setTimeout(() => saveUserTimezone(session.user.id, createdAt), 0);
        }

        // Write the signup consent audit row for OAuth signups (no-op unless a
        // fresh pending consent was stashed just before the redirect).
        if (event === 'SIGNED_IN' && session?.user) {
          const uid = session.user.id;
          setTimeout(() => { void persistPendingSignupConsent(uid); }, 0);
        }
      }
    );

    return () => {
      subscription.unsubscribe();
    };
  }, []);

  // 3-stage auth bootstrap:
  // A) Offline fast-path: use IndexedDB backup directly without network calls
  // B) getSession() fast path
  // C) refreshSession() once
  // D) stalled -> show recovery UI with retry (no sign-out, no storage clears)
  useEffect(() => {
    let cancelled = false;
    const bootStart = performance.now();

    setLoading(true);
    setAuthLoading('auth', true);
    setBootstrapStatus('booting');
    setBootstrapReason(null);
    // IMPORTANT: do not set isAuthReady=false if it was already ready with a real session.
    // This prevents flicker during token refresh events.
    if (!session?.user) setIsAuthReady(false);

    // First-time visitors on slow connections need more time to bootstrap.
    // Check if this is likely a first visit (no stored tokens = new user).
    const isFirstTimeVisitor = !hasStoredAuthToken();
    const hardTimeoutMs = isFirstTimeVisitor ? 15000 : 10000;
    
    const hardTimeout = window.setTimeout(() => {
      if (cancelled) return;
      // If we still don't have a deterministic result, break the spinner.
      if (!isAuthReadyRef.current && !hasSessionRef.current) {
        if (import.meta.env.DEV) {
          logger.warn('[AuthBootstrap] Hard timeout hit; entering stalled state');
        }
        setBootstrapStatus('stalled');
        setBootstrapReason('timeout');
        setLoading(false);
        setAuthLoading('auth', false);
      }
    }, hardTimeoutMs);

    const run = async () => {
      try {
        const isOffline = !navigator.onLine;
        
        // STAGE A: Offline fast-path - skip network calls entirely and use cached session
        if (isOffline) {
          if (import.meta.env.DEV) {
            logger.log('[AuthBootstrap] Offline detected, using cached session...');
          }
          
          // First check localStorage for existing Supabase session
          const tokenExists = hasStoredAuthToken();
          if (tokenExists) {
            const storedSession = readStoredSupabaseSession();
            if (storedSession?.user) {
              offlineSessionRestoredRef.current = true;
              setSession(storedSession);
              setUser(storedSession.user);
              setBootstrapStatus('ready');
              setBootstrapReason('session_found');
              setIsAuthReady(true);
              setLoading(false);
              setAuthLoading('auth', false);
              setTimeout(() => {
                const rememberMe = localStorage.getItem('lupuslogix_remember_me') !== 'false';
                sessionBackupStore.backupSession(storedSession, rememberMe);
              }, 0);
              if (import.meta.env.DEV) {
                logger.log('[AuthBootstrap] Offline: using raw stored local session');
              }
              return;
            }

            // Try to get cached session from Supabase's localStorage
            try {
              // CRITICAL: wrap in timeout — offline, supabase-js may hang attempting
              // internal refresh/network checks even though we just want the cached
              // session. Without this, the splash freezes until the 10s hard timeout.
              const res = await withTimeout(
                supabase.auth.getSession(),
                2000,
                'offline_getSession_timeout'
              );
              const cachedSession = res?.data?.session;
              if (cachedSession?.user) {
                setSession(cachedSession);
                setUser(cachedSession.user);
                setBootstrapStatus('ready');
                setBootstrapReason('session_found');
                setIsAuthReady(true);
                setLoading(false);
                setAuthLoading('auth', false);
                if (import.meta.env.DEV) {
                  logger.log('[AuthBootstrap] Offline: using cached localStorage session');
                }
                return;
              }
            } catch (err) {
              // getSession may fail/timeout offline, continue to IndexedDB
              if (import.meta.env.DEV) {
                logger.warn('[AuthBootstrap] Offline: getSession failed/timeout, falling back to IndexedDB:', err instanceof Error ? err.name : String(err));
              }
            }
          }
          
          // Try IndexedDB backup - Session object has user property
          try {
            const backupSession = await withTimeout(
              sessionBackupStore.restoreSession(),
              2000,
              'indexeddb_restore_timeout'
            );
            if (backupSession?.user && backupSession?.access_token) {
              // Use the stored user object directly for offline mode
              offlineSessionRestoredRef.current = true;
              setSession(backupSession);
              setUser(backupSession.user);
              setBootstrapStatus('ready');
              setBootstrapReason('session_found');
              setIsAuthReady(true);
              setLoading(false);
              setAuthLoading('auth', false);
              if (import.meta.env.DEV) {
                logger.log('[AuthBootstrap] Offline: restored session from IndexedDB backup');
              }
              return;
            }
          } catch (backupErr) {
            if (import.meta.env.DEV) {
              logger.warn('[AuthBootstrap] Offline: IndexedDB restore failed:', backupErr);
            }
          }
          
          // No cached session available offline - this is first-ever offline launch
          // Set a special bootstrap reason so UI can show appropriate message
          setSession(null);
          setUser(null);
          setBootstrapStatus('ready');
          setBootstrapReason('offline_no_session');
          setIsAuthReady(true);
          setLoading(false);
          setAuthLoading('auth', false);
          logOfflineBootstrapPath('offline_no_session', { source: 'auth_bootstrap' });
          if (import.meta.env.DEV) {
            logger.log('[AuthBootstrap] Offline: no cached session (first-ever offline launch)');
          }
          return;
        }
        
        // STAGE B: getSession (online)
        const t0 = performance.now();
        let getSessionResult: Awaited<ReturnType<typeof supabase.auth.getSession>> | null = null;
        try {
          getSessionResult = await withTimeout(supabase.auth.getSession(), 3000, 'getSession_timeout');
        } catch (err) {
          if (import.meta.env.DEV) {
            logger.warn('[AuthBootstrap] getSession failed/timeout:', err instanceof Error ? err.name : String(err));
          }
        }

        if (cancelled) return;

        const getSessionElapsed = performance.now() - t0;
        if (import.meta.env.DEV) {
          logger.log('[AuthBootstrap] getSession elapsed_ms:', Math.round(getSessionElapsed));
        }

        const sessionFromGet = getSessionResult?.data?.session ?? null;
        if (sessionFromGet?.user) {
          setSession(sessionFromGet);
          setUser(sessionFromGet.user);
          setBootstrapStatus('ready');
          setBootstrapReason('session_found');
          setIsAuthReady(true);
          setLoading(false);
          setAuthLoading('auth', false);
          return;
        }

        // If getSession succeeded and definitively found no session, confirm via refresh once
        // (refresh will also cover the case where tokens exist but session isn't hydrated yet).
        setBootstrapStatus('refreshing');

        const t1 = performance.now();
        let refreshResult: Awaited<ReturnType<typeof supabase.auth.refreshSession>> | null = null;
        let refreshErr: unknown = null;

        try {
          refreshResult = await withTimeout(
            supabase.auth.refreshSession(),
            4000,
            'refreshSession_timeout'
          );
        } catch (err) {
          refreshErr = err;
        }

        if (cancelled) return;

        const refreshElapsed = performance.now() - t1;
        if (import.meta.env.DEV) {
          logger.log('[AuthBootstrap] refreshSession_ran:', true);
          logger.log('[AuthBootstrap] refreshSession elapsed_ms:', Math.round(refreshElapsed));
        }

        const sessionFromRefresh = refreshResult?.data?.session ?? null;
        const refreshError = refreshResult?.error ?? refreshErr;

        if (sessionFromRefresh?.user) {
          setSession(sessionFromRefresh);
          setUser(sessionFromRefresh.user);
          setBootstrapStatus('ready');
          setBootstrapReason('session_found');
          setIsAuthReady(true);
          setLoading(false);
          setAuthLoading('auth', false);
          if (import.meta.env.DEV) {
            logger.log('[AuthBootstrap] time_to_session_restored_ms:', Math.round(performance.now() - bootStart));
          }
          return;
        }

        // If we have no tokens, or refresh confirms an invalid refresh token, we can safely treat as signed out.
        const tokenExistsNow = hasStoredAuthToken();
        const definitiveNoSession =
          !tokenExistsNow || isDefinitivelyNoSessionError(refreshError);

        if (import.meta.env.DEV) {
          logger.log('[AuthBootstrap] refreshSession_result_session:', sessionFromRefresh ? 'exists' : 'null');
          logger.log('[AuthBootstrap] token_key_exists_after:', tokenExistsNow);
        }

        // Stage C: Try IndexedDB session backup (for Android PWA cold starts)
        // IMPORTANT: Wrap IndexedDB operations in timeout to prevent slow storage from triggering stalled state
        if (definitiveNoSession) {
          if (import.meta.env.DEV) {
            logger.log('[AuthBootstrap] Checking IndexedDB session backup...');
          }
          
          try {
            // Timeout IndexedDB restore to 2s to prevent blocking on slow storage
            const backupSession = await withTimeout(
              sessionBackupStore.restoreSession(),
              2000,
              'indexeddb_restore_timeout'
            );
            if (backupSession?.refresh_token) {
              // Try to restore session using the backed-up refresh token
              if (import.meta.env.DEV) {
                logger.log('[AuthBootstrap] Found IndexedDB backup, attempting restore...');
              }
              
              const { data: restoredSession, error: restoreError } = await withTimeout(
                supabase.auth.setSession({
                  access_token: backupSession.access_token,
                  refresh_token: backupSession.refresh_token,
                }),
                4000,
                'setSession_timeout'
              );
              
              if (restoredSession?.session?.user && !restoreError) {
                setSession(restoredSession.session);
                setUser(restoredSession.session.user);
                setBootstrapStatus('ready');
                setBootstrapReason('session_found');
                setIsAuthReady(true);
                setLoading(false);
                setAuthLoading('auth', false);
                if (import.meta.env.DEV) {
                  logger.log('[AuthBootstrap] Session restored from IndexedDB backup!');
                }
                return;
              } else {
                // Backup was stale/invalid, clear it (don't await - fire and forget)
                if (import.meta.env.DEV) {
                  logger.log('[AuthBootstrap] IndexedDB backup invalid, clearing');
                }
                sessionBackupStore.clearBackup().catch(() => {});
              }
            }
          } catch (backupErr) {
            // Timeout or error - continue without backup (don't block auth)
            if (import.meta.env.DEV) {
              logger.warn('[AuthBootstrap] IndexedDB restore failed/timeout:', backupErr instanceof Error ? backupErr.name : String(backupErr));
            }
          }
          
          // No valid session anywhere
          setSession(null);
          setUser(null);
          setBootstrapStatus('ready');
          setBootstrapReason('no_session');
          setIsAuthReady(true);
          setLoading(false);
          setAuthLoading('auth', false);
          return;
        }

        // Stage D: stalled (transient failures) -> show recovery UI; do NOT sign out.
        setBootstrapStatus('stalled');
        setBootstrapReason(refreshError ? 'error' : 'timeout');
        setLoading(false);
        setAuthLoading('auth', false);
        if (import.meta.env.DEV) {
          logger.warn('[AuthBootstrap] Entering stalled state (will not redirect to /auth)');
        }
      } catch (err) {
        if (cancelled) return;
        setBootstrapStatus('stalled');
        setBootstrapReason('error');
        setLoading(false);
        setAuthLoading('auth', false);
        logger.error('[AuthBootstrap] Unexpected error:', err);
      }
    };

    void run();

    return () => {
      cancelled = true;
      window.clearTimeout(hardTimeout);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [bootstrapNonce]);

  const retryAuthInit = () => {
    if (import.meta.env.DEV) {
      logger.log('[AuthBootstrap] Manual retry requested');
    }
    setBootstrapNonce((n) => n + 1);
  };

  const signIn = async (email: string, password: string, honeypot?: string) => {
    // Bot detection via honeypot
    if (honeypot && honeypot.length > 0) {
      logger.warn('Bot detected via honeypot');
      // Silently fail for bots
      await new Promise(resolve => setTimeout(resolve, 2000));
      return { error: new Error('Invalid credentials') };
    }

    // Check rate limit
    const rateLimitCheck = await checkRateLimit({
      action: 'login',
      identifier: email
    });

    if (!rateLimitCheck.allowed) {
      toast.error(rateLimitCheck.error || 'Too many login attempts');
      return { error: new Error(rateLimitCheck.error) };
    }

    const { data, error } = await supabase.auth.signInWithPassword({
      email,
      password,
    });
    
    if (error) {
      // Check if this is an approved beta applicant who hasn't signed up yet
      if (error.message === 'Invalid login credentials') {
        const { data: isApproved } = await supabase
          .rpc('is_beta_application_accepted', { p_email: email.trim() });
        
        if (isApproved) {
          // Check if they actually have an account
          const { data: existingUser } = await supabase.auth.signInWithPassword({
            email,
            password: 'check_if_user_exists_dummy_password_that_will_fail'
          });
          
          // If we get "Invalid login credentials", user exists but wrong password
          // If we get here with isApproved, guide them appropriately
          toast.error("Incorrect password. If you haven't created an account yet, please use the Sign Up tab.");
          return { error: new Error('Invalid credentials') };
        } else {
          toast.error("This email is not approved. Registration is invitation only at this time.");
          return { error: new Error('Not approved') };
        }
      }
      
      toast.error(error.message);
      logError({
        message: 'Login failed',
        stack: error.message,
        url: window.location.href
      });
      return { error };
    }
    
    // Verify user has beta access or is admin
    if (data.user) {
      // Check all access sources in parallel to avoid blocking admins
      const [betaTesterResult, adminResult, testAccountResult, acknowledgmentStatus] = await Promise.all([
        supabase.from('beta_testers').select('id').eq('user_id', data.user.id).maybeSingle(),
        supabase.rpc('has_role', { _user_id: data.user.id, _role: 'admin' }),
        supabase.from('test_accounts').select('id, is_active').eq('user_id', data.user.id).eq('is_active', true).maybeSingle(),
        getBetaAcknowledgmentStatus(data.user.id),
      ]);

      let betaTester = betaTesterResult.data;
      const isAdmin = !!adminResult.data;
      const testAccount = testAccountResult.data;

      if (acknowledgmentStatus.gateCheckError) {
        await supabase.auth.signOut({ scope: 'local' });
        toast.error('Please try signing in again so we can verify your beta acknowledgment.');
        return { error: new Error('Beta acknowledgment verification failed') };
      }

      if (acknowledgmentStatus.isUnsignedBetaTester) {
        cacheAccessResult(data.user.id, true);
        markBetaAcknowledgmentFreshSignIn(data.user.id);
        toast.info('Beta acknowledgment required. Please review and acknowledge before continuing.');
        return { error: null };
      }

      // If already has access, cache result and proceed immediately
      if (betaTester || isAdmin || testAccount) {
        cacheAccessResult(data.user.id, true);
        toast.success('Welcome back!');
        return { error: null };
      }

      // Not in beta_testers, admin, or test_accounts - check if they have an approved application
      const { data: approved } = await supabase
        .rpc('is_beta_application_accepted', { p_email: email.trim() });
      const isApproved = !!approved;

      if (isApproved) {
        // Grant beta benefits for approved applicant
        const { error: grantError } = await supabase.rpc('grant_beta_benefits', {
          p_user_id: data.user.id,
          p_phase: 'phase_2'
        });

        if (!grantError) {
          // Re-check beta_testers after granting
          const { data: newBetaTester } = await supabase
            .from('beta_testers')
            .select('id')
            .eq('user_id', data.user.id)
            .maybeSingle();
          betaTester = newBetaTester;
        }
      }

      if (!betaTester && !isAdmin && !testAccount) {
        // IMPORTANT: For approved applicants, don't destroy their session.
        // Cache access result so ProtectedRoute can use it immediately.
        if (isApproved) {
          cacheAccessResult(data.user.id, true);
          toast.success("Welcome! Setting up your account...");
          return { error: null };
        }

        // Not approved: sign them out to prevent access.
        await supabase.auth.signOut();
        toast.error(
          "This email is not approved. Registration is invitation only at this time."
        );
        return { error: new Error('Access denied - not approved') };
      }
      
      // User has access - cache it
      cacheAccessResult(data.user.id, true);
    }
    
    toast.success('Welcome back!');
    return { error: null };
  };

  const signUp = async (email: string, password: string, honeypot?: string) => {
    // Bot detection via honeypot
    if (honeypot && honeypot.length > 0) {
      logger.warn('Bot detected via honeypot');
      // Silently fail for bots
      await new Promise(resolve => setTimeout(resolve, 2000));
      return { error: new Error('Signup failed') };
    }

    // Check rate limit
    const rateLimitCheck = await checkRateLimit({
      action: 'signup',
      identifier: email
    });

    if (!rateLimitCheck.allowed) {
      toast.error(rateLimitCheck.error || 'Too many signup attempts');
      return { error: new Error(rateLimitCheck.error) };
    }

    const redirectUrl = `${window.location.origin}/auth`;
    
    const { error } = await supabase.auth.signUp({
      email,
      password,
      options: {
        emailRedirectTo: redirectUrl
      }
    });
    
    if (error) {
      // Provide clearer error messages
      if (error.message === 'User already registered') {
        toast.error("An account with this email already exists. Please use the Sign In tab instead.");
      } else {
        toast.error(error.message);
      }
      logError({
        message: 'Signup failed',
        stack: error.message,
        url: window.location.href
      });
      return { error };
    }
    
    // Note: Beta validation and beta_testers record creation is handled by the calling component
    // (Auth.tsx or AuthForm.tsx) BEFORE calling signUp and AFTER successful signup.
    // We don't sign out or validate here to avoid race conditions.

    // PostHog: track new signup for retention cohort analysis.
    try { analytics.userSignedUp({ method: 'email' }); } catch {}

    return { error: null };
  };

  const signOut = async () => {
    // Reset prefetch tracking so next login starts fresh
    resetPrefetchedTabs();
    
    // Clear session backup from IndexedDB
    await sessionBackupStore.clearBackup();
    
    // Clear persisted query cache from IndexedDB
    if (user?.id) {
      await clearQueryCache(user.id);
    }
    
    const { error } = await supabase.auth.signOut();
    if (error) {
      toast.error(error.message);
    } else {
      toast.success('Signed out successfully');
    }
  };

  const value = useMemo(() => ({
    user,
    session,
    loading,
    isAuthReady,
    bootstrapStatus,
    bootstrapReason,
    retryAuthInit,
    signIn,
    signUp,
    signOut,
  }), [user, session, loading, isAuthReady, bootstrapStatus, bootstrapReason, retryAuthInit, signIn, signUp, signOut]);

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
